// src/components/BankPosting/api/bankPostingApi.js
// ============================================================================
// BLOCK BP.API.001 — Bank Posting API client
// ----------------------------------------------------------------------------
// PURPOSE
// - Centralizes all Bank Posting HTTP requests in one feature-local module
// - Keeps axios URL details out of page/components/controller render logic
// - Normalizes response shapes so UI code can rely on safe defaults
//
// EDITING GUIDE
// - Small shared primitives first
// - Read/query calls second
// - Mutation/write calls last
// ============================================================================

import axios from '../../../axiosClient.js';

// ============================================================================
// BLOCK BP.API.001.01 — Primitive request/response helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Keep repeated trim / fallback logic out of individual API functions
// - Normalize ids and string payload values before sending to backend
// ============================================================================
const trimStr = (value) => String(value ?? '').trim();
const normalizeTransactionKey = (value) => trimStr(value);

// ============================================================================
// BLOCK BP.API.001.02 — Read-only Bank Posting queries
// ----------------------------------------------------------------------------
// PURPOSE
// - Fetch left-panel transaction queue
// - Fetch removed-items panel rows
// - Fetch selected transaction detail payload for right-side panels
// ============================================================================

/**
 * Loads grouped bank transactions for the main left tree.
 *
 * RESPONSE SHAPE
 * - groups: [{ date, transactions: [...] }]
 * - totalTransactions: numeric total returned by backend
 */
export async function fetchBankTransactions(params = {}) {
  const { data } = await axios.get('/api/bank-posting/transactions', { params });
  return {
    groups: Array.isArray(data?.groups) ? data.groups : [],
    totalTransactions: Number(data?.total_transactions ?? 0),
  };
}

/**
 * Loads removed/ignored items for the bottom-left removed-items panel.
 */
export async function fetchRemovedItems(params = {}) {
  const { data } = await axios.get('/api/bank-posting/removed-items', { params });
  return Array.isArray(data?.items) ? data.items : [];
}

/**
 * Loads selected transaction detail payload.
 *
 * PANELS FED BY THIS RESPONSE
 * - Suggested invoices
 * - Open invoices
 * - Selected/assigned invoices already linked under the transaction
 * - Pagination metadata for suggested/open lists
 */
export async function fetchTransactionSuggestions(transactionKey, params = {}) {
  const { data } = await axios.get(
    `/api/bank-posting/transactions/${normalizeTransactionKey(transactionKey)}/suggestions`,
    {
      params,
    }
  );
  return {
    transaction: data?.transaction || null,
    suggestedInvoices: Array.isArray(data?.suggested_invoices) ? data.suggested_invoices : [],
    openInvoices: Array.isArray(data?.open_invoices) ? data.open_invoices : [],
    selectedInvoices: Array.isArray(data?.selected_invoices) ? data.selected_invoices : [],
    suggestedPagination: data?.suggested_pagination || null,
    openPagination: data?.open_pagination || null,
  };
}

/**
 * Searches historical posted cash receipts for the right-panel past-postings list.
 *
 * IMPORTANT
 * - Returns empty rows when search is blank so callers can skip expensive queries.
 */
export async function fetchPastPostings(params = {}) {
  const search = trimStr(params?.search);

  if (!search) {
    return {
      items: [],
      totalRows: 0,
    };
  }

  const { data } = await axios.get('/api/bank-posting/past-postings', {
    params: {
      ...params,
      search,
    },
  });

  return {
    items: Array.isArray(data?.items) ? data.items : [],
    totalRows: Number(data?.total_rows ?? 0),
  };
}

/**
 * Runs the explicit backend auto-match job.
 *
 * MODES
 * - preserveManual=true: clears generated auto results and reruns matching
 * - startOver=true: restores default state by clearing assignments/state first
 */
export async function runAutoMatch(payload = {}) {
  const { data } = await axios.post('/api/bank-posting/auto-match/run', payload);
  return {
    ok: Boolean(data?.ok),
    summary: data?.summary || null,
    scope: data?.scope || null,
  };
}

/**
 * Polls the server for the current auto-match run's per-transaction progress.
 * Returns { runId, processed, total, running } reflecting live backend state.
 */
export async function fetchAutoMatchProgress() {
  const { data } = await axios.get('/api/bank-posting/auto-match/progress');
  return {
    runId: data?.runId ?? null,
    processed: Number(data?.processed ?? 0),
    total: Number(data?.total ?? 0),
    running: Boolean(data?.running),
  };
}

/**
 * Rebuilds invoice-side Bank Posting match seeds from the backend.
 */
export async function rebuildMatchSeeds() {
  const { data } = await axios.post('/api/bank-posting/admin/rebuild-match-seeds');
  return {
    ok: Boolean(data?.ok),
    summary: data?.summary || null,
  };
}

/**
 * Restores cleared/removed state for transactions that were submitted as cash
 * receipts but had their state rows wiped by a previous Start Over run.
 * Pass { dry_run: true } to preview without committing.
 */
export async function restoreSubmittedState(payload = {}) {
  const { data } = await axios.post(
    '/api/bank-posting/auto-match/restore-submitted-state',
    payload
  );
  return {
    ok: Boolean(data?.ok),
    restored_count: Number(data?.restored_count ?? 0),
    skipped_count: Number(data?.skipped_count ?? 0),
    dry_run: Boolean(data?.dry_run),
  };
}

// ============================================================================
// BLOCK BP.API.001.03 — Transaction and invoice mutation calls
// ----------------------------------------------------------------------------
// PURPOSE
// - Persist parent transaction state changes
// - Persist invoice assignment/removal
// - Persist inline child discount edits
// - Restore / clear removed items
// ============================================================================

/**
 * Persists editable payment/discount values for an already-assigned invoice row.
 */
export async function updateAssignedInvoice(transactionKey, linkId, payload) {
  const sanitizedPayload = {
    ...payload,
    invoice_discount:
      payload?.invoice_discount === '' ||
      payload?.invoice_discount === null ||
      payload?.invoice_discount === undefined
        ? null
        : Number(Number(payload.invoice_discount).toFixed(2)),
    applied_amount:
      payload?.applied_amount === '' ||
      payload?.applied_amount === null ||
      payload?.applied_amount === undefined
        ? null
        : Number(Number(payload.applied_amount).toFixed(2)),
  };

  const { data } = await axios.post(
    `/api/bank-posting/transactions/${normalizeTransactionKey(transactionKey)}/assigned-invoices/${linkId}`,
    sanitizedPayload
  );
  return data;
}

/**
 * Creates or updates BankTransactionState for one transaction.
 */
export async function updateTransactionState(transactionKey, payload) {
  const { data } = await axios.post(
    `/api/bank-posting/transactions/${normalizeTransactionKey(transactionKey)}/state`,
    payload
  );
  return data;
}

/**
 * Persists selected suggested/open invoices under a transaction.
 */
export async function assignInvoicesToTransaction(transactionKey, invoices) {
  const { data } = await axios.post(
    `/api/bank-posting/transactions/${normalizeTransactionKey(transactionKey)}/assign-invoices`,
    {
      invoices,
    }
  );
  return data;
}

/**
 * Removes linked invoice rows from a transaction.
 *
 * IMPORTANT
 * - invoiceKeys may contain both regular LOKA keys and manual open-invoice keys
 * - Keys are trimmed before POST so backend parsing remains predictable
 */
export async function removeInvoicesFromTransaction(transactionKey, invoiceKeys) {
  const { data } = await axios.post(
    `/api/bank-posting/transactions/${normalizeTransactionKey(transactionKey)}/remove-invoices`,
    {
      invoice_keys: invoiceKeys.map((row) => trimStr(row)),
    }
  );
  return data;
}

/**
 * Stages checked zero-balance parent transactions into cash-receipt batch/detail tables.
 */
export async function submitCashReceipts(payload) {
  const { data } = await axios.post('/api/bank-posting/submit-cash-receipts', payload);
  return data;
}

/**
 * Stages a negative reversal entry from one historical posted deposit/batch.
 */
export async function reversePastPosting(payload) {
  const { data } = await axios.post('/api/bank-posting/past-postings/reverse', payload);
  return data;
}

/**
 * Restores one removed item back into the active queue.
 */
export async function restoreRemovedItem(stateId) {
  const id = Number(stateId);

  if (!Number.isFinite(id)) {
    throw new Error('Invalid removed item state id.');
  }

  const { data } = await axios.post(`/api/bank-posting/removed-items/${id}/restore`);
  return data;
}

/**
 * Marks checked removed items as cleared.
 */
export async function clearRemovedItems(stateIds) {
  const { data } = await axios.post('/api/bank-posting/removed-items/clear', {
    state_ids: stateIds,
  });
  return data;
}

/**
 * Imports normalized CSV rows as new bank transactions with duplicate skipping.
 */
export async function importBankTransactionCsvDescriptions(rows) {
  const { data } = await axios.post('/api/bank-posting/import-transaction-descriptions', {
    rows: Array.isArray(rows) ? rows : [],
  });

  return {
    ok: Boolean(data?.ok),
    rowsReceived: Number(data?.rows_received ?? 0),
    rowsNormalized: Number(data?.rows_normalized ?? 0),
    rowsEligible: Number(data?.rows_eligible ?? 0),
    insertedRows: Number(data?.inserted_rows ?? 0),
    duplicateRows: Number(data?.duplicate_rows ?? 0),
    duplicateExistingRows: Number(data?.duplicate_existing_rows ?? 0),
    duplicateFileRows: Number(data?.duplicate_file_rows ?? 0),
    duplicateInFileNewDateRows: Number(data?.duplicate_in_file_new_date_rows ?? 0),
    skippedTimeRows: Number(data?.skipped_time_rows ?? 0),
    matchedRows: Number(data?.matched_rows ?? 0),
    updatedRows: Number(data?.updated_rows ?? 0),
    unchangedRows: Number(data?.unchanged_rows ?? 0),
    skippedRows: Number(data?.skipped_rows ?? 0),
    ambiguousRows: Number(data?.ambiguous_rows ?? 0),
    invalidRows: Number(data?.invalid_rows ?? 0),
    overrideInserts: Number(data?.override_inserts ?? 0),
    overrideUpdates: Number(data?.override_updates ?? 0),
    overridesUpserted: Number(data?.overrides_upserted ?? 0),
    details: Array.isArray(data?.details) ? data.details : [],
  };
}

/**
 * Updates or creates a customer alias with priority/score = 100.
 */
export async function fetchCustomerAliases(params = {}) {
  const { data } = await axios.get('/api/bank-posting/customer-aliases', {
    params: {
      customer_no: trimStr(params?.customer_no || ''),
      ar_division_no: trimStr(params?.ar_division_no || '00'),
    },
  });

  return Array.isArray(data?.aliases) ? data.aliases : [];
}

/**
 * Updates or creates a customer alias with priority/score = 100.
 */
export async function updateCustomerAlias(payload) {
  const { data } = await axios.post('/api/bank-posting/update-alias', payload);
  return data;
}

/**
 * Removes a customer alias line from the alias table.
 */
export async function removeCustomerAlias(payload) {
  const { data } = await axios.post('/api/bank-posting/remove-alias', payload);
  return data;
}
